import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Separator } from '~/components/ui/separator';
import Loading from '../components/base/Loading';
import Content from '../components/layout/Content';

const TreatmentNewVariant = () => {
    const params = useParams();
    const navigate = useNavigate();

    const id = params.id!;

    const treatmentQuery = trpc.treatments.getOne.useQuery(
        {
            id: id,
        },
        {
            suspense: true,
        }
    );

    const createTreatmentVariantMutation = trpc.treatmentVariants.create.useMutation({
        onSuccess: (data) => {
            toast.success('Variant created successfully.');
            treatmentQuery.refetch();

            navigate(`/treatment/${id}/variants/${data.data.id}`)
        },
        onError: (error) => {
            toast.error(`Failed to create variant: ${error.message}`);
        },
    });

    const handleCreateTreatmentVariant = async (data: { name: string; shortDescription: string; longDescription?: string }) => {
        createTreatmentVariantMutation.mutate({
            treatmentId: id,
            ...data,
        });
    };

    if (treatmentQuery.isLoading) return <Loading />;

    if (!treatmentQuery.data)
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='flex flex-col items-center justify-center min-h-[60vh] text-center'>
                        <div className='p-4 bg-red-100 rounded-full mb-6'>
                            <AlertCircle className='h-16 w-16 text-red-600' />
                        </div>
                        <h1 className='text-4xl font-bold text-gray-900 mb-4'>Ups...</h1>
                        <p className='text-xl text-gray-600 mb-8'>Etwas ist schief gelaufen</p>
                        <Button variant='default' onClick={() => navigate(-1)}>
                            Zurück
                        </Button>
                    </div>
                </div>
            </Content>
        );

    return (
        <Content>
            <div className='max-w-6xl container mx-auto'>
                <div className='flex justify-between items-center mb-4'>
                    <div className='flex flex-row items-center gap-3'>
                        <Button variant='ghost' size='icon' onClick={() => navigate(`/treatment/${id}`)} className='w-8 h-8 border-gray-300 hover:bg-gray-50'>
                            <ArrowLeft />
                        </Button>
                        <h2 className='text-3xl font-semibold mb-1.5'>Add Variant</h2>
                    </div>
                </div>

                <div className='flex gap-4'>
                    {/* Left Column - 25% width */}
                    <div className='w-1/4 space-y-4'>
                        {/* Basic Information Card */}
                        <Card className='bg-white shadow-sm pb-0'>
                            <CardContent className='p-0'>
                                <div className='flex flex-col'>
                                    <div className='flex flex-col px-6 gap-1'>
                                        <h2 className='text-2xl font-semibold mb-1.5'>{treatmentQuery.data.data.name}</h2>
                                        <Badge className='bg-green-200 text-green-800'>Active</Badge>
                                        <p className='text-sm text-muted-foreground'>
                                            {treatmentQuery.data.data.variants.length} {treatmentQuery.data.data.variants.length === 1 ? 'variant' : 'variants'}
                                        </p>
                                    </div>

                                    <Separator className='mt-4' />

                                    {treatmentQuery.data.data.variants.map((variant, index, array) => (
                                        <a
                                            key={`variant_link_${variant.id}`}
                                            href={`/treatment/${treatmentQuery.data.data.id}/variants/${variant.id}`}
                                            className={`flex flex-row px-4 py-3 text-sm ${index === array.length - 1 ? 'rounded-b-lg' : ''} !text-gray-700 hover:!bg-gray-100 transition-all`}
                                        >
                                            <small className='text-sm font-medium leading-none'>{variant.name}</small>
                                        </a>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - 75% width */}
                    <div className='w-3/4 space-y-4'>
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <h3 className="text-xl font-semibold mb-4">Create new variant</h3>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const formData = new FormData(e.currentTarget);
                                        const fields = {
                                            name: formData.get('name') as string,
                                            shortDescription: formData.get('shortDescription') as string,
                                            longDescription: formData.get('longDescription') as string,
                                            categoryId: formData.get('categoryId') ? Number(formData.get('categoryId')) : null,
                                        };
                                        handleCreateTreatmentVariant(fields);
                                    }}
                                >
                                    <div className='space-y-6'>
                                        <div className='space-y-2'>
                                            <label htmlFor='name' className='text-sm font-medium'>
                                                Name
                                            </label>
                                            <Input id='name' name='name' className='w-full' required />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='shortDescription' className='text-sm font-medium'>
                                                Short Description
                                            </label>
                                            <Input id='shortDescription' name='shortDescription' className='w-full' />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='longDescription' className='text-sm font-medium'>
                                                Long Description
                                            </label>
                                            <textarea
                                                id='longDescription'
                                                name='longDescription'
                                                className='w-full min-h-[150px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                                            />
                                        </div>

                                        <div className='flex justify-end mt-6'>
                                            <Button
                                                type='submit'
                                                className='bg-primary-400 hover:bg-primary-500 !text-white transition-all'
                                                disabled={createTreatmentVariantMutation.isPending}
                                            >
                                                {createTreatmentVariantMutation.isPending && <Loader2 className='animate-spin mr-2 h-4 w-4' />}
                                                Save Variant
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </Content>
    );
};

export default TreatmentNewVariant;
